import React, { useState } from 'react';
import { Search, ShieldCheck, ShieldAlert, ShieldX, ExternalLink, Loader2 } from 'lucide-react';

// CARRIER CHECK — look up a trucking company's public FMCSA record.
//
// Runs entirely in the browser. FMCSA publishes these as open Socrata datasets on
// data.transportation.gov with `Access-Control-Allow-Origin: *` and no API key, so
// there is no backend, no secret to provision, and nothing to deploy alongside it.
//
// TWO DATASETS, and they do NOT share a key format — this is the trap:
//   az4n-8mr2  Company Census   dot_number is PLAIN      ("169844")
//   6eyk-hxee  Authority + insurance  dot_number is ZERO-PADDED TO 8 ("00169844")
// Querying the second one with an unpadded number returns an empty array, which
// would render as "no operating authority on file" — a false red flag on a
// perfectly legitimate carrier, on a page whose whole job is telling drivers who
// is legitimate. Always pad. Verified against Swift Transportation Co. of Arizona
// (DOT 54283 → "00054283").
//
// WHAT THIS COVERS: registration status, the FMCSA SAFETY RATING, prior-revoked
// registration, operating authority (carrier vs broker), and insurance filings.
//
// WHAT IT DOES NOT COVER, and why the UI says so: crash history and
// out-of-service rates live in FMCSA's SMS/CSA data, which is not in either of
// these datasets — so the result links to the official SAFER snapshot for the
// full record rather than implying a clean bill of health. Overstating this would
// be worse than not shipping it: a driver reads a green panel as "this company is
// fine" and takes the job.
//
// The safety rating was missed on the first cut of this file and it is the single
// most important field here. DOT 169451 is active, has active authority and full
// insurance on file — and is rated CONDITIONAL. Without reading `safety_rating`
// it rendered as "nothing alarming in the public record".

const CENSUS = 'https://data.transportation.gov/resource/az4n-8mr2.json';
const AUTHORITY = 'https://data.transportation.gov/resource/6eyk-hxee.json';

const padDot = (dot) => String(dot || '').replace(/\D/g, '').padStart(8, '0');
const num = (v) => {
  const n = parseInt(String(v ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
};

// Socrata SoQL string literals escape a single quote by doubling it. Without this
// a company with an apostrophe ("O'BRIEN TRUCKING") produces a 400.
const soqlEscape = (s) => String(s).replace(/'/g, "''");

async function getJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`FMCSA returned ${res.status}`);
  return res.json();
}

async function searchByName(name) {
  const q = soqlEscape(name.trim().toUpperCase());
  const where = encodeURIComponent(`upper(legal_name) like '%${q}%' OR upper(dba_name) like '%${q}%'`);
  const select = encodeURIComponent('legal_name,dba_name,dot_number,phy_city,phy_state,power_units,total_drivers,status_code');
  // ::number is load-bearing. power_units is a TEXT column in this dataset, so a
  // plain `$order=power_units DESC` sorts LEXICOGRAPHICALLY — "9" ranks above
  // "12940", and with $limit=8 the actual carrier a driver searched for falls off
  // the end entirely while namesakes with 9 trucks fill the list. Verified:
  // "swift" without the cast omits Swift Transportation of Arizona (12,940 trucks);
  // with it, Swift Canada and Swift AZ come first.
  return getJson(`${CENSUS}?$where=${where}&$select=${select}&$order=power_units::number DESC&$limit=8`);
}

async function loadCarrier(dotNumber) {
  const dot = String(dotNumber).replace(/\D/g, '');
  const [censusRows, authRows] = await Promise.all([
    getJson(`${CENSUS}?dot_number=${encodeURIComponent(dot)}`),
    // Padded — see the note at the top of this file.
    getJson(`${AUTHORITY}?dot_number=${encodeURIComponent(padDot(dot))}`)
  ]);
  if (!censusRows.length) return null;
  // ALL authority rows, not the first. A DOT can hold several — METRO PARK
  // WAREHOUSES (DOT 144293) returns one broker-only row and one with active
  // common authority, in an order we don't control. Taking [0] made the verdict a
  // coin flip on exactly the entities where it matters most.
  return { census: censusRows[0], authorities: authRows };
}

// ─── Reading the record ─────────────────────────────────────────────────────
// FMCSA uses single-letter codes. Translating them is most of the value here —
// "common_stat: A" means nothing to a driver; "authority is active" does.

// FMCSA safety rating. Assigned after a compliance review, and it is the single
// most important thing on this page — a Conditional or Unsatisfactory rating is
// exactly what a driver is trying to find out before they take a seat. Most
// carriers are UNRATED (never reviewed), which is normal and is NOT a negative.
const SAFETY_RATING = {
  S: { level: 'good', text: 'FMCSA safety rating: SATISFACTORY.' },
  C: {
    level: 'bad',
    text: 'FMCSA safety rating: CONDITIONAL — the carrier was reviewed and found out of compliance in at least one area. Ask what it was and whether it has been corrected.'
  },
  U: {
    level: 'bad',
    text: 'FMCSA safety rating: UNSATISFACTORY — the most serious rating FMCSA issues. Do not sign anything before you understand why.'
  }
};

function readRecord({ census, authorities }) {
  const flags = [];
  const registrationActive = (census.status_code || '').toUpperCase() === 'A';

  // carrier_operation: A = interstate, B = intrastate hazmat, C = intrastate
  // non-hazmat. This distinction decides whether the FEDERAL filings below are
  // even required: about 1.7 million of the ~4.5 million carriers in this file
  // are intrastate, and they file authority and insurance with their STATE, not
  // with FMCSA. Treating a missing federal filing as a red flag for those would
  // wrongly warn a driver off a perfectly legitimate local carrier — the largest
  // false-positive class in this whole check.
  //
  // The field is sometimes absent entirely. Absent means UNKNOWN, not interstate:
  // we soften to a warning rather than assert a problem we cannot substantiate.
  const op = (census.carrier_operation || '').toUpperCase();
  const isInterstate = op === 'A';
  const opKnown = op === 'A' || op === 'B' || op === 'C';
  const federalFilingsRequired = isInterstate;
  const missingFilingLevel = federalFilingsRequired ? 'bad' : 'warn';
  const intrastateNote = federalFilingsRequired
    ? ''
    : opKnown
      ? ' This carrier is registered for intrastate work, so it files with its state rather than FMCSA — ask to see the state filing.'
      : ' FMCSA does not list whether this carrier runs interstate or intrastate, so this may simply be filed with the state instead.';

  if (!registrationActive) {
    flags.push({
      level: 'bad',
      text: 'FMCSA lists this DOT registration as INACTIVE. A carrier that is hiring on an inactive registration is worth asking about directly.'
    });
  }

  // The safety rating, read from the census row we already have.
  const rating = (census.safety_rating || '').toUpperCase();
  if (SAFETY_RATING[rating]) {
    const y = (census.safety_rating_date || '').slice(0, 4);
    flags.push({
      level: SAFETY_RATING[rating].level,
      text: SAFETY_RATING[rating].text + (y ? ` Rated ${y}.` : '')
    });
  }

  // A carrier that previously had its authority revoked is the "chameleon
  // carrier" signal — re-registering under a new DOT to shed a bad record.
  if ((census.prior_revoke_flag || '').toUpperCase() === 'Y') {
    flags.push({
      level: 'warn',
      text: 'FMCSA flags a PRIOR REVOKED registration tied to this carrier. Sometimes an ordinary lapse, sometimes a company re-registering to leave a record behind. Worth asking about.'
    });
  }

  // BROKER authority is deliberately NOT counted as carrier authority. A broker
  // arranges freight; it cannot lawfully run trucks on that authority. Folding
  // broker_stat into this check made the widget clear a broker-only entity as
  // having "active operating authority" for a driver about to take a seat.
  const anyStat = (field) =>
    (authorities || []).some((a) => (a?.[field] || '').toUpperCase() === 'A');
  const hasActiveAuthority = anyStat('common_stat') || anyStat('contract_stat');
  const brokerOnly = !hasActiveAuthority && anyStat('broker_stat');
  const authority = (authorities || [])[0] || null;

  if (brokerOnly) {
    flags.push({
      level: 'warn',
      text: 'This entity holds active BROKER authority but no active motor-carrier authority. Brokers arrange freight; they do not run trucks. Make sure you know which company you would actually be driving for.'
    });
  }

  if (authorities?.length && !hasActiveAuthority && !brokerOnly) {
    flags.push({
      level: missingFilingLevel,
      text: `No ACTIVE operating authority on file with FMCSA.${
        federalFilingsRequired
          ? ' Interstate carriers need active authority to haul for hire.'
          : intrastateNote
      }`
    });
  }
  if (!authority) {
    flags.push({
      level: 'warn',
      text: `No federal operating-authority record found.${
        federalFilingsRequired
          ? ' Unusual for an interstate carrier — ask about it.'
          : intrastateNote
      }`
    });
  }

  // Insurance: both fields are in THOUSANDS of dollars in the FMCSA file. Taken
  // as the MAX across every authority row, for the same reason the status checks
  // are — one DOT can carry several rows and the highest filing is the real one.
  const onFile = Math.max(0, ...(authorities || []).map((a) => num(a?.bipd_file))) * 1000;
  const required = Math.max(0, ...(authorities || []).map((a) => num(a?.min_cov_amount))) * 1000;
  if (authorities?.length) {
    if (onFile === 0) {
      flags.push({
        level: missingFilingLevel,
        text: `No liability insurance on file with FMCSA.${
          federalFilingsRequired
            ? ' This is the single biggest thing to ask about before you drive for anyone.'
            : intrastateNote
        }`
      });
    } else if (required > 0 && onFile < required) {
      flags.push({
        level: 'warn',
        text: `Insurance on file ($${onFile.toLocaleString()}) is below the required minimum ($${required.toLocaleString()}).`
      });
    }
  }

  const trucks = num(census.power_units);
  const drivers = num(census.total_drivers);
  if (trucks > 0 && drivers > trucks * 3) {
    flags.push({
      level: 'warn',
      text: `Reports ${drivers.toLocaleString()} drivers against ${trucks.toLocaleString()} trucks. Sometimes stale paperwork, sometimes a sign of heavy turnover — ask.`
    });
  }

  // Verdict is driven by the WORST flag present, not by flag count — adding the
  // satisfactory-rating flag above must not knock a clean carrier down to "worth
  // asking about".
  const hasBad = flags.some((f) => f.level === 'bad');
  const hasWarn = flags.some((f) => f.level === 'warn');

  if (!hasBad && !hasWarn) {
    flags.push({
      level: 'good',
      text:
        'Registration and operating authority are active, and liability insurance is on file' +
        // Don't claim it meets a minimum we don't know. min_cov_amount is often 0.
        (required > 0 ? ' at or above the required minimum.' : '.')
    });
  }

  // Most carriers have never been rated, and that is normal — say so rather than
  // letting a reader infer that no rating means a clean one.
  if (!SAFETY_RATING[rating]) {
    flags.push({
      level: 'info',
      text: 'No FMCSA safety rating on file. Most carriers have never had a compliance review — this is common and is not a negative mark.'
    });
  }

  return {
    verdict: hasBad ? 'bad' : hasWarn ? 'warn' : 'good',
    flags,
    onFile,
    required,
    trucks,
    drivers,
    cdlDrivers: num(census.total_cdl),
    since: (census.add_date || '').slice(0, 4)
  };
}

const VERDICT = {
  good: { Icon: ShieldCheck, cls: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', label: 'Nothing alarming in the public record' },
  warn: { Icon: ShieldAlert, cls: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', label: 'Worth asking about' },
  bad: { Icon: ShieldX, cls: 'text-red-700', bg: 'bg-red-50 border-red-200', label: 'Check this before you sign anything' }
};

export default function CarrierCheck() {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [matches, setMatches] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setMatches(null); setResult(null); setError(''); };

  const pick = async (dotNumber) => {
    setBusy(true);
    reset();
    try {
      const data = await loadCarrier(dotNumber);
      if (!data) setError('No FMCSA record for that DOT number.');
      else setResult({ ...data, read: readRecord(data) });
    } catch (err) {
      setError(err.message || 'Could not reach FMCSA just now.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setBusy(true);
    reset();
    try {
      // All digits → treat as a DOT number. Otherwise search by name.
      if (/^\d+$/.test(q.replace(/\s/g, ''))) {
        const data = await loadCarrier(q);
        if (!data) setError(`No FMCSA record for DOT ${q.replace(/\D/g, '')}.`);
        else setResult({ ...data, read: readRecord(data) });
      } else {
        const rows = await searchByName(q);
        if (!rows.length) setError(`No carrier found matching “${q}”. Try the DOT number from the job ad.`);
        else if (rows.length === 1) await pick(rows[0].dot_number);
        else setMatches(rows);
      }
    } catch (err) {
      setError(err.message || 'Could not reach FMCSA just now.');
    } finally {
      setBusy(false);
    }
  };

  const c = result?.census;
  const r = result?.read;
  const V = r ? VERDICT[r.verdict] : null;

  return (
    <div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <input
          className="flex-1 rounded-brand border border-hair bg-white px-5 py-4 text-ink placeholder:text-slateink/60 focus:border-brass focus:outline-none"
          placeholder="Company name or DOT number"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Carrier name or DOT number"
        />
        <button type="submit" className="av-btn-brass px-8 py-4 text-sm" disabled={busy || !query.trim()}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {busy ? 'Checking…' : 'Check carrier'}
        </button>
      </form>

      {error && (
        <p className="mt-4 rounded-brand border border-hair bg-warm px-5 py-4 text-slateink">{error}</p>
      )}

      {matches && (
        <div className="mt-6">
          <p className="mb-3 text-sm text-slateink">Several carriers match. Pick the one from the job ad:</p>
          <ul className="divide-y divide-hair border-t border-b border-hair">
            {matches.map((m) => (
              <li key={m.dot_number}>
                <button
                  type="button"
                  onClick={() => pick(m.dot_number)}
                  className="w-full py-4 text-left hover:text-brass"
                >
                  <span className="font-semibold">{m.legal_name}</span>
                  {m.dba_name && <span className="text-slateink"> — dba {m.dba_name}</span>}
                  <span className="block text-sm text-slateink">
                    DOT {m.dot_number} · {[m.phy_city, m.phy_state].filter(Boolean).join(', ')} ·{' '}
                    {num(m.power_units).toLocaleString()} trucks
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result && (
        <div className="mt-6">
          <div className={`rounded-brand border p-6 ${V.bg}`}>
            <div className={`flex items-center gap-3 ${V.cls}`}>
              <V.Icon className="w-6 h-6 shrink-0" />
              <span className="font-semibold text-lg">{V.label}</span>
            </div>
            <h3 className="mt-4 text-xl font-bold text-ink">{c.legal_name}</h3>
            {c.dba_name && <p className="text-slateink">doing business as {c.dba_name}</p>}
            <p className="mt-1 text-sm text-slateink">
              DOT {c.dot_number}
              {result.authority?.docket_number ? ` · ${result.authority.docket_number}` : ''} ·{' '}
              {[c.phy_city, c.phy_state].filter(Boolean).join(', ')}
              {r.since ? ` · registered since ${r.since}` : ''}
            </p>

            <ul className="mt-5 space-y-2">
              {r.flags.map((f) => (
                <li key={f.text} className="flex gap-2 text-sm">
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      f.level === 'bad'
                        ? 'bg-red-600'
                        : f.level === 'warn'
                          ? 'bg-amber-500'
                          : f.level === 'info'
                            ? 'bg-slateink'
                            : 'bg-emerald-600'
                    }`}
                  />
                  <span className="text-ink">{f.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-hair pt-5">
              <div>
                <div className="text-2xl font-bold text-ink">{r.trucks.toLocaleString()}</div>
                <div className="text-xs text-slateink">Trucks</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-ink">{r.drivers.toLocaleString()}</div>
                <div className="text-xs text-slateink">Drivers</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-ink">{r.cdlDrivers.toLocaleString()}</div>
                <div className="text-xs text-slateink">CDL drivers</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-ink">
                  {r.onFile ? `$${(r.onFile / 1000).toLocaleString()}k` : '—'}
                </div>
                <div className="text-xs text-slateink">Insurance on file</div>
              </div>
            </div>
          </div>

          {/* Say plainly what this check does NOT include. A driver reading "nothing
              alarming" must not think that covers the safety rating or crash history. */}
          <p className="mt-4 text-sm text-slateink font-light leading-relaxed">
            This covers registration, operating authority, insurance filings and the FMCSA safety
            rating. It does <strong className="text-ink">not</strong> include crash history or
            out-of-service rates, which live in FMCSA’s SMS data — for those, open the official
            snapshot below. And ask Alfred: it goes through this with you and asks the carrier the
            questions this record can’t answer.
          </p>
          <a
            className="mt-3 inline-flex items-center gap-2 text-brass font-semibold hover:text-brassdeep"
            href={`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${String(c.dot_number).replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Official FMCSA snapshot for DOT {c.dot_number} <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  );
}
